import React, { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export type NewsItem = {
  title: string;
  link: string;
  source: string;
  date: string;
  category: string;
  editorial?: boolean;
};

export type NewsGroup = {
  category: string;
  query: string;
  items: NewsItem[];
};

const initialEditorialSources: Record<string, NewsItem[]> = {
  "Mercado financeiro": [
    {
      title: "Mercados, juros, crédito e investimentos: acompanhe os movimentos que influenciam sua estratégia.",
      link: "https://www.infomoney.com.br/ultimas-noticias/",
      source: "InfoMoney",
      date: "",
      category: "Mercado financeiro",
      editorial: true,
    },
    {
      title: "Economia brasileira, decisões financeiras e seus impactos sobre empresas e negócios.",
      link: "https://agenciabrasil.ebc.com.br/economia",
      source: "Agência Brasil",
      date: "",
      category: "Mercado financeiro",
      editorial: true,
    },
  ],
  "Liderança": [
    {
      title: "Gestão de pessoas, carreira e as decisões que definem uma liderança mais preparada.",
      link: "https://exame.com/carreira/",
      source: "Exame",
      date: "",
      category: "Liderança",
      editorial: true,
    },
  ],
  "Empresas e negócios": [
    {
      title: "Empresas, empreendedorismo e oportunidades que estão movimentando os negócios.",
      link: "https://exame.com/negocios/",
      source: "Exame",
      date: "",
      category: "Empresas e negócios",
      editorial: true,
    },
    {
      title: "Acompanhe notícias sobre empresas, economia e o ambiente de negócios brasileiro.",
      link: "https://valor.globo.com/",
      source: "Valor Econômico",
      date: "",
      category: "Empresas e negócios",
      editorial: true,
    },
  ],
  "Mundo corporativo": [
    {
      title: "Inovação, economia e tendências para entender os próximos movimentos do mercado.",
      link: "https://agenciabrasil.ebc.com.br/economia",
      source: "Agência Brasil",
      date: "",
      category: "Mundo corporativo",
      editorial: true,
    },
  ],
};

const initialFeeds: NewsGroup[] = [
  {
    category: "Mercado financeiro",
    query: "mercado financeiro Brasil investimentos crédito",
    items: initialEditorialSources["Mercado financeiro"],
  },
  {
    category: "Liderança",
    query: "liderança gestão pessoas executivos",
    items: initialEditorialSources["Liderança"],
  },
  {
    category: "Empresas e negócios",
    query: "empresas negócios Brasil mercado",
    items: initialEditorialSources["Empresas e negócios"],
  },
  {
    category: "Mundo corporativo",
    query: "economia inovação empreendedorismo mundo corporativo",
    items: initialEditorialSources["Mundo corporativo"],
  },
];

function formatDate(date: string, lng: string = "pt") {
  if (!date) return "Recente";
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "Recente";
  try {
    const locale = lng === "en" ? "en-US" : lng === "es" ? "es-ES" : "pt-BR";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })
      .format(value)
      .replace(".", "");
  } catch {
    return "Recente";
  }
}

function executiveView(category: string, lng: string = "pt") {
  if (lng === "en") {
    if (category.includes("Mercado") || category.includes("Market"))
      return "Observe how shifting macro indicators impact borrowing costs, liquidity, and capital allocation.";
    if (category.includes("Liderança") || category.includes("Leadership"))
      return "Reflect on how this theme influences organizational culture, accountability, and high-stakes execution.";
    if (category.includes("Empresas") || category.includes("Business"))
      return "Evaluate emerging risks, strategic moats, and competitive maneuvers across your sector.";
    return "Synthesize the data within your business reality and make informed moves with poise.";
  }

  if (lng === "es") {
    if (category.includes("Mercado") || category.includes("Market"))
      return "Analiza cómo el escenario macroeconómico afecta el crédito, la tesorería y las inversiones estratégicas.";
    if (category.includes("Liderança") || category.includes("Leadership"))
      return "Reflexiona sobre cómo esta agenda impacta en las personas, la cultura de equipo y la toma de decisiones.";
    if (category.includes("Empresas") || category.includes("Business"))
      return "Evalúa riesgos, oportunidades y movimientos que pueden transformar la estrategia empresarial.";
    return "Conecta la información con tu contexto operativo y decide con conciencia y método.";
  }

  if (category === "Mercado financeiro")
    return "Observe como o cenário pode afetar crédito, caixa, custos e decisões de investimento.";
  if (category === "Liderança")
    return "Reflita sobre como essa pauta impacta pessoas, cultura, comunicação e tomada de decisão.";
  if (category === "Empresas e negócios")
    return "Analise riscos, oportunidades e movimentos que podem mudar a estratégia do negócio.";
  return "Conecte a informação ao seu contexto e decida com consciência antes de agir.";
}

export function RadarPage() {
  const { t, i18n } = useTranslation();
  const currentLng = (i18n.resolvedLanguage || i18n.language || "pt").split("-")[0];

  const [groups, setGroups] = useState<NewsGroup[]>(initialFeeds);
  const [hasLiveNews, setHasLiveNews] = useState(false);
  const [featured, setFeatured] = useState<NewsItem | null>(null);
  const [updatedAt, setUpdatedAt] = useState(() => {
    try {
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date());
    } catch {
      return "";
    }
  });

  useEffect(() => {
    document.title = "Radar Executivo | Notícias do Mundo Corporativo";
    window.scrollTo({ top: 0, behavior: "smooth" });

    let isMounted = true;
    async function fetchRadar() {
      try {
        const res = await fetch("/api/radar");
        if (!res.ok) return;
        const data = (await res.json()) as { groups: NewsGroup[]; hasLiveNews: boolean; updatedAt: string };
        if (!isMounted) return;

        if (data.groups && data.groups.length > 0) {
          setGroups(data.groups);
          setHasLiveNews(data.hasLiveNews);
          if (data.updatedAt) setUpdatedAt(data.updatedAt);

          if (data.hasLiveNews) {
            const allItems = data.groups.flatMap((g) => g.items.filter((it) => !it.editorial));
            if (allItems.length > 0) {
              const sorted = [...allItems].sort(
                (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
              );
              setFeatured(sorted[0] || null);
            }
          }
        }
      } catch (err) {
        console.error("Erro ao carregar Radar:", err);
      }
    }

    fetchRadar();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <main>
      <section className="radar-hero live-radar-hero">
        <div>
          <p className="eyebrow">{t("radar.hero.eyebrow")}</p>
          <h1>
            {t("radar.hero.title").split(".")[0]}
            <br />
            <em>{t("radar.hero.title")}</em>
          </h1>
          <p>{t("radar.hero.subtitle")}</p>
          <div className="live-indicator">
            <i></i>{" "}
            {hasLiveNews
              ? t("radar.hero.liveNews")
              : t("radar.hero.editorialSources")}
          </div>
        </div>
        <div className="radar-orbit">
          <div className="radar-screen" aria-hidden="true">
            <div className="radar-sweep"></div>
            <div className="radar-ring inner-ring"></div>
            <div className="radar-axis axis-h"></div>
            <div className="radar-axis axis-v"></div>
          </div>
          
          <span className="orbit-item item-mercado">
            <i className="orbit-dot"></i>
            {t("radar.orbit.mercado")}
          </span>
          <span className="orbit-item item-financas">
            <i className="orbit-dot"></i>
            {t("radar.orbit.financas")}
          </span>
          <span className="orbit-item item-empresas">
            <i className="orbit-dot"></i>
            {t("radar.orbit.empresas")}
          </span>
          
          <div className="radar-center">
            <strong>
              RADAR
              <br />
              EXECUTIVO
            </strong>
          </div>
          
          <span className="orbit-item item-lideranca">
            <i className="orbit-dot"></i>
            {t("radar.orbit.lideranca")}
          </span>
          <span className="orbit-item item-negocios">
            <i className="orbit-dot"></i>
            {t("radar.orbit.negocios")}
          </span>
        </div>
      </section>

      <section className="news-area">
        <div className="news-heading">
          <div>
            <p className="eyebrow">{t("radar.newsArea.eyebrow")}</p>
            <h2>
              {t("radar.newsArea.title").split(".")[0]}.
              <br />
              <em>{t("radar.newsArea.title").split(".")[1] || ""}</em>
            </h2>
          </div>
          <p>
            {hasLiveNews
              ? t("radar.newsArea.liveSubtitle")
              : t("radar.newsArea.editorialSubtitle")}
            <br />
            <span className="radar-updated">
              {t("radar.newsArea.lastUpdate")} {updatedAt}
            </span>
          </p>
        </div>

        <nav className="radar-filters" aria-label="Categorias de notícias">
          {groups.map((group, index) => (
            <a href={`#editoria-${index}`} key={group.category}>
              {group.category}
            </a>
          ))}
        </nav>

        {featured && (
          <a className="featured-news" href={featured.link} target="_blank" rel="noopener noreferrer">
            <div>
              <p className="eyebrow">
                DESTAQUE · {featured.category.toUpperCase()}
              </p>
              <h3>{featured.title}</h3>
              <span>
                {featured.source} · {formatDate(featured.date, currentLng)}
              </span>
            </div>
            <div>
              <strong>{t("radar.newsArea.executiveView")}</strong>
              <p>{executiveView(featured.category, currentLng)}</p>
              <b>{t("radar.newsArea.readFull")}</b>
            </div>
          </a>
        )}

        {groups.map((group, groupIndex) => (
          <section className="news-group" id={`editoria-${groupIndex}`} key={group.category}>
            <div className="news-group-title">
              <h3>{group.category}</h3>
              <span>
                {group.items.some((item) => item.editorial)
                  ? t("radar.newsArea.selectedSources")
                  : t("radar.newsArea.newsCount", { count: group.items.length })}
              </span>
            </div>
            <div className="live-news-grid">
              {group.items.map((item, index) => (
                <a
                  className="live-news-card"
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={`${item.link}-${index}`}
                >
                  <div className="news-meta">
                    <span>{item.category}</span>
                    {item.date ? (
                      <time>{formatDate(item.date, currentLng)}</time>
                    ) : (
                      <span>EDITORIA</span>
                    )}
                  </div>
                  <h4>{item.title}</h4>
                  <p className="news-source">
                    {t("radar.newsArea.sourcePrefix")} {item.source}
                  </p>
                  <div className="executive-insight">
                    <strong>{t("radar.newsArea.executiveView")}</strong>
                    <p>{executiveView(item.category, currentLng)}</p>
                  </div>
                  <b>
                    {item.editorial
                      ? t("radar.newsArea.accessEditorial")
                      : t("radar.newsArea.readFull")}
                  </b>
                </a>
              ))}
            </div>
          </section>
        ))}
      </section>

      <section className="radar-method">
        <div>
          <p className="eyebrow">{t("radar.method.eyebrow")}</p>
          <h2>
            {t("radar.method.title").split(".")[0]}.
            <br />
            <em>{t("radar.method.title").split(".")[1] || ""}</em>
          </h2>
        </div>
        <div className="radar-process">
          <article>
            <span>01</span>
            <h3>{t("radar.method.step1Title")}</h3>
            <p>{t("radar.method.step1Desc")}</p>
          </article>
          <article>
            <span>02</span>
            <h3>{t("radar.method.step2Title")}</h3>
            <p>{t("radar.method.step2Desc")}</p>
          </article>
          <article>
            <span>03</span>
            <h3>{t("radar.method.step3Title")}</h3>
            <p>{t("radar.method.step3Desc")}</p>
          </article>
        </div>
      </section>

      <section className="radar-cta">
        <p className="eyebrow">{t("radar.cta.eyebrow")}</p>
        <h2>
          {t("radar.cta.title").split(".")[0]}.
          <br />
          <em>{t("radar.cta.title").split(".")[1] || ""}</em>
        </h2>
        <Link to="/adult" className="button">
          {t("radar.cta.button")}
        </Link>
      </section>
    </main>
  );
}
